<template>
  <div class="pa-2 fleet-id-tags-panel">
    <v-alert v-if="!canManageIdTags" type="info" :value="true" class="mb-3">
      Only admins can manage fleet idTag mappings.
    </v-alert>

    <v-card flat>
      <v-card-text>
        <v-layout row wrap align-center>
          <v-flex xs12 sm5 md4 pa-2>
            <v-text-field
              v-model="search"
              label="Search idTag, plate, or shortcode"
              append-icon="fal fa-search"
              clearable
              hide-details
              :loading="isLoading"
              @keyup.enter.native="applySearch"
              @click:append="applySearch"
            />
          </v-flex>
          <v-spacer />
          <v-flex xs12 sm4 md3 pa-2 class="text-xs-right">
            <v-btn v-if="canManageIdTags" color="primary" :disabled="actionInProgress" @click="openCreateDialog">
              Add ID Tag
            </v-btn>
          </v-flex>
        </v-layout>
      </v-card-text>
      <v-data-table
        :headers="headers"
        :items="mappings"
        :pagination.sync="pagination"
        :total-items="totalMappings"
        :loading="isLoading"
        :rows-per-page-items="[10, 25, 50]"
        @update:pagination="onPagination"
        must-sort
      >
        <template v-slot:items="{ item }">
          <tr>
            <td>{{ item.idTag || '-' }}</td>
            <td>{{ item.licensePlateNumber || '-' }}</td>
            <td>{{ item.shortcodeId || '-' }}</td>
            <td>{{ item.vehicleClass || '-' }}</td>
            <td>{{ item.fingerprintStatus || '-' }}</td>
            <td>{{ formatDateTime(item.dateUpdated || item.dateCreated) }}</td>
            <td class="text-xs-right">
              <v-btn v-if="canManageIdTags" icon small title="Edit" :disabled="actionInProgress" @click="editMapping(item)">
                <v-icon small>fal fa-edit</v-icon>
              </v-btn>
              <v-btn v-if="canManageIdTags" icon small title="Delete" :disabled="actionInProgress" @click="deleteMapping(item)">
                <v-icon small color="error">fal fa-trash</v-icon>
              </v-btn>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card>

    <ocpp-id-tag-dialog
      :value="dialogVisible"
      :mapping="selectedMapping"
      @input="onDialogInput"
    />
  </div>
</template>

<script>
import { formatDate, formatTime } from '@smarttransit/common'
import { addAlert, isAuthorized } from '@/utilities/helpers'
import {
  listOcppIdTagAssignments,
  countOcppIdTagAssignments,
  deleteOcppIdTagAssignment
} from '@/services/ocpp-id-tag-service'
import OcppIdTagDialog from '@/components/ocpp-id-tag-dialog/template'

export default {
  name: 'fleet-id-tags-panel',
  components: {
    OcppIdTagDialog
  },
  props: {
    signedInUser: Object
  },
  data () {
    return {
      isLoading: false,
      actionInProgress: false,
      dialogVisible: false,
      selectedMapping: null,
      mappings: [],
      totalMappings: 0,
      search: '',
      pagination: {
        descending: false,
        page: 1,
        rowsPerPage: 10,
        sortBy: 'idTag'
      },
      headers: [
        { text: 'idTag', value: 'idTag', sortable: true },
        { text: 'License Plate', value: 'licensePlateNumber', sortable: true },
        { text: 'Shortcode', value: 'shortcodeId', sortable: false },
        { text: 'Vehicle Class', value: 'vehicleClass', sortable: true },
        { text: 'Fingerprint', value: 'fingerprintStatus', sortable: true },
        { text: 'Updated', value: 'dateUpdated', sortable: true },
        { text: 'Actions', value: 'actions', sortable: false, align: 'right' }
      ],
      vehicleClasses: [
        { text: 'Fleet bus', value: 'fleet_bus' },
        { text: 'Private vehicle', value: 'private_vehicle' }
      ]
    }
  },
  computed: {
    canManageIdTags () {
      return isAuthorized(this.signedInUser, 'admin')
    }
  },
  mounted () {
    this.refreshMappings()
  },
  methods: {
    focusPanel () {
      return this.refreshMappings()
    },
    formatDateTime (value) {
      if (!value) {
        return '-'
      }
      return formatTime(value) + ', ' + formatDate(value)
    },
    buildOrder () {
      const columnMap = {
        idTag: 'id_tag',
        licensePlateNumber: 'license_plate_number',
        vehicleClass: 'vehicle_class',
        fingerprintStatus: 'fingerprint_status',
        dateUpdated: 'date_updated'
      }
      const sortBy = columnMap[this.pagination.sortBy] || 'id_tag'
      const sortDirection = this.pagination.descending ? 'DESC' : 'ASC'
      return sortBy + ' ' + sortDirection
    },
    async refreshMappings () {
      if (!this.canManageIdTags) {
        this.mappings = []
        this.totalMappings = 0
        return
      }

      this.isLoading = true
      try {
        const page = this.pagination.page || 1
        const rowsPerPage = this.pagination.rowsPerPage || 10
        const offset = (page - 1) * rowsPerPage
        const search = this.search || undefined
        const order = this.buildOrder()
        const result = await Promise.all([
          listOcppIdTagAssignments({
            search,
            limit: rowsPerPage,
            offset,
            order
          }),
          countOcppIdTagAssignments({ search })
        ])

        this.mappings = result[0] || []
        this.totalMappings = result[1] && result[1].count ? Number(result[1].count) : 0
      } catch (err) {
        addAlert({ message: err && err.message ? err.message : 'Unable to load OCPP idTags', type: 'error' })
      } finally {
        this.isLoading = false
      }
    },
    applySearch () {
      this.pagination.page = 1
      return this.refreshMappings()
    },
    onPagination () {
      return this.refreshMappings()
    },
    openCreateDialog () {
      this.selectedMapping = null
      this.dialogVisible = true
    },
    editMapping (item) {
      this.selectedMapping = item || null
      this.dialogVisible = true
    },
    async onDialogInput (isOpen) {
      this.dialogVisible = isOpen
      if (isOpen) {
        return
      }
      this.selectedMapping = null
      await this.refreshMappings()
    },
    async deleteMapping (item) {
      if (!item || !item.idTag) {
        return
      }
      if (!confirm('Delete idTag mapping for ' + item.idTag + '?')) {
        return
      }

      this.actionInProgress = true
      try {
        await deleteOcppIdTagAssignment({
          idTag: item.idTag,
          transportationProfileId: item.transportationProfileId
        })
        addAlert({ message: 'OCPP idTag mapping deleted', type: 'success' })
        if (this.selectedMapping && this.selectedMapping.idTag === item.idTag) {
          this.selectedMapping = null
        }
        await this.refreshMappings()
      } catch (err) {
        addAlert({ message: err, type: 'error' })
      } finally {
        this.actionInProgress = false
      }
    }
  }
}
</script>
